// SYSTEM_ROUTE: /problems
import { useState } from 'react';
import type {
  ControlCategory,
  LimiterType,
  Problem,
  ProblemStatus,
  RepairPath,
} from '@life-os/app-core';
import {
  CONTROL_CATEGORIES,
  LIMITER_TYPES,
  PROBLEM_STATUSES,
  isProblemOpen,
  recommendedPath,
  summarizeProblems,
} from '@life-os/app-core';
import { Button, Chip, EmptyState, Pane, Stack } from '@life-os/ui';
import type { ChipTone } from '@life-os/ui';
import { useServices, useStore } from '../app/store.js';
import { useAsync } from '../lib/useAsync.js';
import { ScreenHeader } from '../app/ScreenHeader.js';

const CONTROL_LABEL: Record<ControlCategory, string> = {
  control: 'In my control',
  influence: 'I can influence',
  accept: 'To accept',
};

const CONTROL_HINT: Record<ControlCategory, string> = {
  control: 'Directly changeable by my own action.',
  influence: 'I can nudge it, not command it.',
  accept: 'Fixed for now — route around it, calmly.',
};

const LIMITER_LABEL: Record<LimiterType, string> = {
  false: 'False limit',
  fixable: 'Fixable limit',
  real: 'Real limit',
};

const PATH_LABEL: Record<RepairPath, string> = {
  reframe: 'Reframe',
  repair: 'Repair',
  accept: 'Accept',
};

const PATH_TONE: Record<RepairPath, ChipTone> = {
  reframe: 'primary',
  repair: 'warn',
  accept: 'muted',
};

const STATUS_TONE: Record<ProblemStatus, ChipTone> = {
  identified: 'neutral',
  investigating: 'warn',
  repairing: 'warn',
  resolved: 'primary',
  accepted: 'muted',
};

const inputStyle = {
  background: 'var(--lc-surface-container)',
  color: 'var(--lc-on-surface)',
  border: '1px solid var(--lc-outline-variant)',
  borderRadius: 'var(--lc-radius-full)',
  padding: '6px 12px',
  font: 'inherit',
  fontSize: 'var(--lc-text-sm)',
} as const;

function ProblemCard({ problem, onChange }: { problem: Problem; onChange: () => void }) {
  const services = useServices();
  const path = recommendedPath(problem.limiterType);
  const open = isProblemOpen(problem.status);

  return (
    <div className="lc-row" style={{ flexDirection: 'column', alignItems: 'stretch', gap: 8 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--lc-stack-sm)', flexWrap: 'wrap' }}>
        <strong style={{ flex: 1 }}>{problem.title}</strong>
        <Chip tone="neutral">{LIMITER_LABEL[problem.limiterType]}</Chip>
        <Chip tone={PATH_TONE[path]}>{PATH_LABEL[path]}</Chip>
        <select
          value={problem.status}
          onChange={(e) => void services.problems.setStatus(problem.id, e.target.value as ProblemStatus).then(onChange)}
          aria-label="Status"
          style={inputStyle}
        >
          {PROBLEM_STATUSES.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
        <Chip tone={STATUS_TONE[problem.status]}>{open ? 'open' : 'closed'}</Chip>
      </div>
      <dl style={{ margin: 0, display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '2px 12px', fontSize: 'var(--lc-text-sm)' }}>
        <dt style={{ color: 'var(--lc-on-surface-variant)' }}>Cause</dt>
        <dd style={{ margin: 0 }}>{problem.cause || <em style={{ opacity: 0.6 }}>not yet diagnosed</em>}</dd>
        <dt style={{ color: 'var(--lc-on-surface-variant)' }}>Intervention</dt>
        <dd style={{ margin: 0 }}>{problem.intervention || <em style={{ opacity: 0.6 }}>not yet chosen</em>}</dd>
        <dt style={{ color: 'var(--lc-on-surface-variant)' }}>Next step</dt>
        <dd style={{ margin: 0 }}>{problem.nextStep || <em style={{ opacity: 0.6 }}>not yet set</em>}</dd>
      </dl>
    </div>
  );
}

export function ProblemsScreen() {
  const services = useServices();
  const { revision, refresh } = useStore();
  const [title, setTitle] = useState('');
  const [control, setControl] = useState<ControlCategory>('control');
  const [limiter, setLimiter] = useState<LimiterType>('fixable');

  const problems = useAsync<readonly Problem[]>(() => services.problems.list(), [revision]);

  const create = async () => {
    if (title.trim().length === 0) return;
    await services.problems.create({ title, controlCategory: control, limiterType: limiter });
    setTitle('');
    refresh();
  };

  const items = problems.value ?? [];
  const summary = summarizeProblems(items);

  return (
    <>
      <ScreenHeader title={<>Problems & Repair</>} subtitle={<>Name the limiter, place it on the control spectrum, and choose a neutral path — reframe what
            is false, repair what is fixable, accept what is real. No shame, just the next move.</>} />

      <Pane title="Name a limiter" hint="Honest diagnosis beats willpower.">
        <div style={{ display: 'flex', gap: 'var(--lc-stack-sm)', flexWrap: 'wrap', alignItems: 'center' }}>
          <input
            className="lc-cmdbar"
            style={{ flex: '1 1 240px' }}
            value={title}
            placeholder="e.g. I avoid publishing because it has to be perfect"
            onChange={(e) => setTitle(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') void create();
            }}
            aria-label="Problem title"
          />
          <select value={control} onChange={(e) => setControl(e.target.value as ControlCategory)} aria-label="Control category" style={inputStyle}>
            {CONTROL_CATEGORIES.map((c) => (
              <option key={c} value={c}>
                {CONTROL_LABEL[c]}
              </option>
            ))}
          </select>
          <select value={limiter} onChange={(e) => setLimiter(e.target.value as LimiterType)} aria-label="Limiter type" style={inputStyle}>
            {LIMITER_TYPES.map((l) => (
              <option key={l} value={l}>
                {LIMITER_LABEL[l]}
              </option>
            ))}
          </select>
          <Button variant="primary" onClick={() => void create()}>
            Add
          </Button>
        </div>
      </Pane>

      <Pane title="Open limiters" hint={`${summary.open} open · ${summary.growthActionable} growth-actionable`}>
        {items.length === 0 ? (
          <EmptyState title="Nothing flagged">
            When something blocks you, name it here instead of carrying it. Diagnosis turns a vague weight
            into a concrete, neutral next step.
          </EmptyState>
        ) : (
          <Stack>
            {CONTROL_CATEGORIES.map((cat) => {
              const inCat = items.filter((p) => p.controlCategory === cat);
              if (inCat.length === 0) return null;
              return (
                <div key={cat}>
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, margin: '4px 0 8px' }}>
                    <strong>{CONTROL_LABEL[cat]}</strong>
                    <span style={{ color: 'var(--lc-on-surface-variant)', fontSize: 'var(--lc-text-sm)' }}>
                      {CONTROL_HINT[cat]}
                    </span>
                  </div>
                  <Stack>
                    {inCat.map((p) => (
                      <ProblemCard key={String(p.id)} problem={p} onChange={refresh} />
                    ))}
                  </Stack>
                </div>
              );
            })}
          </Stack>
        )}
      </Pane>
    </>
  );
}
